import { Button } from "@/components/flexkit/Button";
import { ExternalLink } from "@/components/podkit/typography/ExternalLink";
import {
    GCPRunnerAdditionalFieldsKeys,
    getConnectionTypeText,
    useApiEndpointHost,
} from "@/components/runners/details/runner-configuration-keys";
import { RunnerPhaseTag } from "@/components/runners/RunnerPhaseTag";
import { type PlainRunner } from "@/queries/runner-queries";
import { useMemo, type FC } from "react";
import { DegradationReason, Info } from "./CloudFormationStackDetails";

export const GCPRunnerDetails: FC<{ runner: PlainRunner }> = ({ runner }) => {
    const dict = Object.fromEntries(runner.status?.additionalInfo.map((info) => [info.key, info.value]) || []);

    const region = dict[GCPRunnerAdditionalFieldsKeys.Region] || "";
    const projectID = dict[GCPRunnerAdditionalFieldsKeys.ProjectID] || "";
    const zones = dict[GCPRunnerAdditionalFieldsKeys.Zones] || "";
    const runnerLogsURL = dict[GCPRunnerAdditionalFieldsKeys.RunnerLogsURL] || "";
    const customProxyDomain = dict[GCPRunnerAdditionalFieldsKeys.RunnerProxyDomain];
    const runnerTemplateBuildVersion = dict[GCPRunnerAdditionalFieldsKeys.RunnerTemplateBuildVersion];
    const apiEndpoint = dict[GCPRunnerAdditionalFieldsKeys.ApiEndpoint] || "";

    // Parse zones array from string format
    const formattedZones = useMemo(() => {
        if (!zones) return "";
        try {
            // Handle both array string format like '["zone1", "zone2"]' and comma-separated format
            if (zones.startsWith("[") && zones.endsWith("]")) {
                const parsed = JSON.parse(zones) as unknown;
                return Array.isArray(parsed) ? parsed.join(", ") : zones;
            }
            return zones;
        } catch {
            return zones;
        }
    }, [zones]);

    const apiEndpointHost = useApiEndpointHost(apiEndpoint);

    return (
        <div className="flex flex-col gap-2 overflow-x-hidden p-[1px]">
            {runner && (
                <Info label="Status">
                    <RunnerPhaseTag runner={runner} />
                </Info>
            )}
            <DegradationReason runner={runner} />

            <Info label="Runner name">{runner.name}</Info>
            <Info label="Runner version">{runner.status?.version}</Info>
            {projectID && <Info label="GCP Project Name">{projectID}</Info>}
            {region && <Info label="Region">{region}</Info>}
            {formattedZones && <Info label="Zones">{formattedZones}</Info>}
            {runnerTemplateBuildVersion && <Info label="Template build version">{runnerTemplateBuildVersion}</Info>}
            {apiEndpointHost && <Info label="Connection type">{getConnectionTypeText(apiEndpointHost)}</Info>}

            {customProxyDomain && <Info label="Proxy domain">{customProxyDomain}</Info>}

            <div className="mt-2 flex gap-2 p-[1px]">
                {runnerLogsURL && (
                    <Button variant="secondary" className="w-fit" asChild>
                        <ExternalLink
                            href={runnerLogsURL}
                            className="flex items-center truncate text-content-primary"
                            iconSize="sm"
                        >
                            Runner Logs
                        </ExternalLink>
                    </Button>
                )}
            </div>
        </div>
    );
};
